import React, { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle, ChevronDown, ChevronUp, ArrowRight } from "lucide-react";
import { Button } from "../ui/button";
import { useBooking, PACKAGES } from "../../context";
import VehicleSelector from "./VehicleSelector";

const PackageCard = ({ packageKey, pkg }) => {
  const { vehicleType, setSelectedPackage, setShowBookingModal } = useBooking();
  const [showAllInterior, setShowAllInterior] = useState(false);
  const [showAllExterior, setShowAllExterior] = useState(false);
  const price = pkg.basePrices[vehicleType] || pkg.basePrices.small;

  const handleSelect = () => {
    setSelectedPackage(packageKey);
    setShowBookingModal(true);
  };

  const interiorToShow = showAllInterior ? pkg.interior : pkg.interior.slice(0, 4);
  const exteriorToShow = showAllExterior ? pkg.exterior : pkg.exterior.slice(0, 3);
  const hiddenInterior = pkg.interior.length - 4;
  const hiddenExterior = pkg.exterior.length - 3;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className={`relative glass rounded-2xl p-6 card-hover ${pkg.popular ? "package-highlight" : ""}`}
      data-testid={`package-card-${packageKey}`}
    >
      {pkg.popular && <div className="most-popular-badge">Most Popular</div>}
      
      <h3 className="text-2xl font-bold mb-1">{pkg.name}</h3>
      {pkg.includes && <p className="text-cyan-400 text-sm mb-2">{pkg.includes}</p>}
      
      <div className="text-4xl font-black gradient-text mb-2">${price}</div>
      <p className="text-slate-400 text-sm mb-6">{pkg.tagline}</p>

      <div className="space-y-4 mb-6">
        <div>
          <h4 className="font-semibold text-cyan-400 mb-2 text-sm">Interior:</h4>
          <ul className="space-y-1">
            {interiorToShow.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                <CheckCircle className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          {hiddenInterior > 0 && !showAllInterior && (
            <button 
              onClick={() => setShowAllInterior(true)}
              className="text-cyan-400 text-sm mt-2 hover:text-cyan-300 flex items-center gap-1"
            >
              <ChevronDown className="w-4 h-4" />
              +{hiddenInterior} more
            </button>
          )}
          {showAllInterior && hiddenInterior > 0 && (
            <button 
              onClick={() => setShowAllInterior(false)}
              className="text-slate-500 text-sm mt-2 hover:text-slate-400 flex items-center gap-1"
            >
              <ChevronUp className="w-4 h-4" />
              Show less
            </button>
          )}
        </div>
        
        <div>
          <h4 className="font-semibold text-cyan-400 mb-2 text-sm">Exterior:</h4>
          <ul className="space-y-1">
            {exteriorToShow.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                <CheckCircle className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          {hiddenExterior > 0 && !showAllExterior && (
            <button 
              onClick={() => setShowAllExterior(true)}
              className="text-cyan-400 text-sm mt-2 hover:text-cyan-300 flex items-center gap-1"
            >
              <ChevronDown className="w-4 h-4" />
              +{hiddenExterior} more
            </button>
          )}
          {showAllExterior && hiddenExterior > 0 && (
            <button 
              onClick={() => setShowAllExterior(false)}
              className="text-slate-500 text-sm mt-2 hover:text-slate-400 flex items-center gap-1"
            >
              <ChevronUp className="w-4 h-4" />
              Show less
            </button>
          )}
        </div>
      </div>

      <Button 
        onClick={handleSelect}
        className={`w-full rounded-full py-3 font-semibold ${
          pkg.popular 
            ? "btn-primary-glow text-slate-900" 
            : "bg-slate-700 hover:bg-slate-600 text-white"
        }`}
        data-testid={`package-select-${packageKey}-btn`}
      >
        <ArrowRight className="w-4 h-4 mr-2" />
        Get Started
      </Button>

      <p className="text-xs text-slate-500 mt-4 text-center">
        *Heavy duty cleaning may include additional charges
      </p>
    </motion.div>
  );
};

const PackagesSection = () => {
  return (
    <section id="packages" className="section-spacing bg-slate-900/50" data-testid="packages-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Detailing <span className="gradient-text">Packages</span>
          </h2>
          <p className="text-slate-400 mb-8">Select your vehicle size to see pricing</p>
          
          <VehicleSelector />
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {Object.entries(PACKAGES).map(([key, pkg]) => (
            <PackageCard key={key} packageKey={key} pkg={pkg} />
          ))}
        </div>
      </div>
    </section>
  );
};

export { PackageCard };
export default PackagesSection;
